import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Database, Download, Search, ChevronLeft, ChevronRight, 
  AlertTriangle, CheckCircle, Eye, Filter, X,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';

// Sort icon component
const SortIcon = ({ field, sortConfig }) => {
  if (sortConfig.field !== field) {
    return <ArrowUpDown className="h-4 w-4 text-muted-foreground/50" />;
  }
  return sortConfig.dir === 'asc' 
    ? <ArrowUp className="h-4 w-4 text-primary" />
    : <ArrowDown className="h-4 w-4 text-primary" />;
};

const SqlNoMaxMemoryReport = () => {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    total_instances: 0,
    sin_configurar: 0,
    configuradas: 0,
    by_edition: []
  });
  
  // Data
  const [instances, setInstances] = useState({ items: [], total: 0, page: 1, pages: 1 });
  
  // Filters
  const [filters, setFilters] = useState({ search: '' });
  const [searchInput, setSearchInput] = useState('');
  const [activeFilter, setActiveFilter] = useState('sin_configurar'); // 'all', 'sin_configurar', 'configuradas'
  
  // Sort
  const [sortConfig, setSortConfig] = useState({ field: 'hostname', dir: 'asc' });

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/reports/sql/no-maxmemory/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch instances
  const fetchInstances = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        page: page.toString(), 
        page_size: '50',
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (activeFilter !== 'all') params.append('filter_type', activeFilter);
      
      const response = await api.get(`/reports/sql/no-maxmemory?${params}`);
      setInstances(response.data);
    } catch (error) {
      console.error('Error fetching instances:', error);
      toast.error('Error al cargar instancias');
    } finally {
      setLoading(false);
    }
  }, [filters, sortConfig, activeFilter]);

  // Initial load
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
    }, 800);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Fetch on filter/sort change
  useEffect(() => {
    fetchInstances(1);
  }, [filters, sortConfig, activeFilter, fetchInstances]);

  // Handle filter click
  const handleFilterClick = (filterType) => {
    setActiveFilter(filterType);
  };

  // Handle sort
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle export
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (activeFilter !== 'all') params.append('filter_type', activeFilter);
      
      const response = await api.get(`/reports/sql/no-maxmemory/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sql_sin_maxmemory_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Exportación completada');
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  // Format memory
  const formatMemory = (mb) => {
    if (!mb) return '-';
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(1)} GB`;
    }
    return `${mb} MB`;
  };

  // Percentage
  const sinConfigurarPercent = stats.total_instances > 0 
    ? Math.round((stats.sin_configurar / stats.total_instances) * 100) 
    : 0;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">SQL Server - Sin Max Memory</h1>
            <p className="page-subtitle">Instancias SQL con Max Memory en valor default (sin configurar)</p>
          </div>
          <Button onClick={handleExport} disabled={exporting} variant="outline" data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Exportando...' : 'Exportar'}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="sql-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleFilterClick('all')}
          >
            <Database className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total_instances}</div>
              <div className="stats-card-label">Total Instancias</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-yellow-500/50 ${activeFilter === 'sin_configurar' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => handleFilterClick('sin_configurar')}
          >
            <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-yellow-500">{stats.sin_configurar}</div>
              <div className="stats-card-label">Sin Configurar ({sinConfigurarPercent}%)</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${activeFilter === 'configuradas' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleFilterClick('configuradas')}
          >
            <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.configuradas}</div>
              <div className="stats-card-label">Configuradas</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filtros:</span>
              </div>
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Buscar hostname, instancia..." 
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="h-9 w-52"
                  data-testid="filter-search"
                />
              </div>
              {searchInput && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => { setSearchInput(''); setFilters({ search: '' }); }}
                >
                  <X className="h-4 w-4 mr-1" /> Limpiar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : instances.items.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500 opacity-50" />
                <p>No hay instancias sin Max Memory configurado</p>
                <p className="text-sm mt-2">Todas las instancias tienen memoria configurada correctamente</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-subtle">
                  <thead>
                    <tr>
                      <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('hostname')}>
                        <div className="flex items-center gap-1">
                          INSTANCIA <SortIcon field="hostname" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th>IP</th>
                      <th className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('edition')}>
                        <div className="flex items-center gap-1">
                          EDICIÓN <SortIcon field="edition" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th className="text-right cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('physical_memory_mb')}>
                        <div className="flex items-center justify-end gap-1">
                          RAM FÍSICA <SortIcon field="physical_memory_mb" sortConfig={sortConfig} />
                        </div>
                      </th>
                      <th className="text-right">MAX MEMORY</th>
                      <th className="text-center">CPUs</th>
                      <th className="text-center">MAX DOP</th>
                      <th className="w-16 text-right">ACCIÓN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {instances.items.map((instance, idx) => (
                      <tr key={`${instance.hostname}-${instance.instance_id}-${idx}`}>
                        <td>
                          <div className="flex items-center gap-2">
                            <Database className="h-4 w-4 text-primary" strokeWidth={1.5} />
                            <span className="font-medium text-primary">{instance.display_name}</span>
                          </div>
                        </td>
                        <td className="font-mono text-sm text-muted-foreground">{instance.ip || '-'}</td>
                        <td>
                          <span className="badge badge-default">{instance.edition}</span>
                        </td>
                        <td className="text-right font-mono text-sm">
                          {formatMemory(instance.physical_memory_mb)}
                        </td>
                        <td className="text-right">
                          <span className="badge badge-warning flex items-center gap-1 justify-end">
                            <AlertTriangle className="h-3 w-3" />
                            SIN CONFIGURAR
                          </span>
                        </td>
                        <td className="text-center text-muted-foreground">{instance.cpu_count || '-'}</td>
                        <td className="text-center text-muted-foreground">{instance.max_dop || '-'}</td>
                        <td className="text-right">
                          <Link to={`/assets/sql/${instance.instance_id}`}>
                            <button className="action-btn" title="Ver detalle" data-testid={`view-${instance.hostname}`}>
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {instances.total > 0 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {instances.total} instancias sin Max Memory configurado
            </div>
            {instances.pages > 1 && (
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fetchInstances(instances.page - 1)} 
                  disabled={instances.page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  {instances.page} / {instances.pages}
                </span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fetchInstances(instances.page + 1)} 
                  disabled={instances.page === instances.pages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SqlNoMaxMemoryReport;
